import { bound } from "@frui.ts/helpers";
import { Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "config";
import CreditExposure from "entities/creditExposure";
import { computed, observable, reaction } from "mobx";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import CreditExposureRepository from "repositories/creditExposureRepository";
import Distributor from "../../../../entities/distributor";
import type CreditExposureType from "entities/creditExposureType";
import DistributorRepository from "../../../../repositories/distributorRepository";
import ListViewModel from "../../../listViewModel";
import CreditExposureCreateViewModel from "./creditExposureCreateViewModel";
import CreditExposureDetailViewModel from "./creditExposureDetailViewModel";
import CreditExposuresFilter from "../../../../entities/filters/creditExposuresFilter";
import OptionsService from "../../../../services/optionsService";
import CreditExposureState from "entities/creditExposureState";
import { formatAmount, formatNumber } from "../../../../utils/localizations";
import CurrentCreditExposure from "../../../../entities/currentCreditExposure";
import type CreditExposureNotificationStatus from "entities/creditExposureNotificationStatus";

const navigationName = "credit-exposures";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [CreditExposureDetailViewModel, CreditExposureCreateViewModel],
})
export default class CreditExposuresViewModel extends ListViewModel<
  CreditExposure,
  CreditExposureDetailViewModel,
  CreditExposureCreateViewModel,
  CreditExposuresFilter
> {
  readonly modelName = "creditExposure";
  @observable distributors: Distributor[] = [];
  @observable currentCreditExposure?: CurrentCreditExposure;
  @observable selectedDistributorId?: string;
  private unsubscribes: Array<() => void> = [];

  constructor(
    protected createViewModelFactory: ReturnType<typeof CreditExposureCreateViewModel.Factory>,
    protected detailViewModelFactory: ReturnType<typeof CreditExposureDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected repository: CreditExposureRepository,
    protected optionsService: OptionsService,
    private distributorRepository: DistributorRepository
  ) {
    super();
    this.navigationName = navigationName;
  }

  @bound
  onAddClick() {
    const createViewModelFactory = this.createViewModelFactory;
    const selectedDistributorId = this.selectedDistributorId;
    if (
      (this.securityService.allowTo("distributors") && this.securityService.allowTo("enums") && selectedDistributorId) ||
      (this.allowWrite && selectedDistributorId)
    ) {
      return () => this.tryActivateChild(createViewModelFactory(selectedDistributorId));
    } else {
      return undefined;
    }
  }

  @bound
  onItemClick() {
    const detailFactory = this.detailViewModelFactory;
    const selectedDistributorId = this.selectedDistributorId;
    if (detailFactory && selectedDistributorId) {
      return (item: CreditExposure) =>
        this.tryActivateChild(detailFactory(item[this.idProperty] as string, selectedDistributorId));
    } else {
      return undefined;
    }
  }

  @bound
  allowItemDetail(item: CreditExposure): boolean {
    return (
      item.state !== CreditExposureState.Canceled &&
      this.securityService.allowTo("distributors") &&
      this.securityService.allowTo("enums")
    );
  }

  @bound
  allowItemDelete(item: CreditExposure): boolean {
    return item.state !== CreditExposureState.Canceled && this.isEditable;
  }

  delete(item: CreditExposure) {
    const repository = this.repository;

    if (repository && this.allowDelete && this.selectedDistributorId) {
      return repository.delete(item, this.selectedDistributorId);
    } else if (this.allowDelete) {
      throw new Error("You have to overload delete method to specify how to delete your items");
    }
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    if (!this.selectedDistributorId) {
      return Promise.resolve({ items: [] } as any);
    }

    this.repository
      .getCurrentCreditExposure(this.selectedDistributorId)
      .then(currentExposure => (this.currentCreditExposure = currentExposure));

    return this.repository.getAll(this.selectedDistributorId, { offset, limit }, this.appliedFilter);
  }

  protected createFilter(): CreditExposuresFilter {
    return new CreditExposuresFilter();
  }

  @computed
  get stateOptions() {
    return this.optionsService.internalEnumOptions(CreditExposureState);
  }

  @computed
  get allowWrite() {
    return (
      (this.securityService.allowTo("distributors") && this.securityService.allowTo("enums")) ||
      this.securityService.allowToEntity(this.modelName, "write")
    );
  }

  @computed
  get isEditable() {
    return this.securityService.allowTo("distributors") && this.securityService.allowTo("enums");
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  @computed
  get totalExposure(): string | undefined {
    return this.currentCreditExposure ? formatAmount(this.currentCreditExposure.total) : undefined;
  }

  @computed
  get reservedExposure(): string | undefined {
    return this.currentCreditExposure ? formatAmount(this.currentCreditExposure.reserved) : undefined;
  }

  @computed
  get remainingExposure(): string | undefined {
    return this.currentCreditExposure ? formatAmount(this.currentCreditExposure.remaining) : undefined;
  }

  @computed
  get usedExposurePercentage(): string | undefined {
    return this.currentCreditExposure ? `${formatNumber(this.currentCreditExposure.remainingPercentage)} %` : undefined;
  }

  @computed
  get creditExposureWarningPercentage(): string | undefined {
    return this.currentCreditExposure ? `${formatNumber(this.currentCreditExposure.warningPercentage)} %` : undefined;
  }

  @computed
  get creditExposureNotificationStatus(): string | undefined {
    return this.currentCreditExposure
      ? this.translateCreditExposureNotificationStatus(this.currentCreditExposure.warningNotificationStatus)
      : undefined;
  }

  @computed
  get isCreditExposureAmountInsufficient(): boolean {
    if (!this.currentCreditExposure) {
      return false;
    }
    return this.currentCreditExposure.remainingPercentage < this.currentCreditExposure.warningPercentage;
  }

  @computed
  get isCreditExposureAmountExhausted(): boolean {
    if (!this.currentCreditExposure || this.currentCreditExposure.total === 0) {
      return false;
    }

    return this.currentCreditExposure.remaining <= 0;
  }

  @watchBusy
  async onInitialize(): Promise<void> {
    this.distributors = (await this.distributorRepository.getAll()).items;
    await super.onInitialize();
    this.unsubscriptions.push(reaction(() => this.filter.distributorId, this.onSelectedDistributorId));
  }

  onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
      this.unsubscribes = [];
    }

    return super.onDeactivate(isClosing);
  }

  @bound
  onSelectedDistributorId(value: string) {
    this.selectedDistributorId = value;
    this.currentCreditExposure = undefined;
    this.items = [];
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `creditExposures.${code}`, size);
  }

  translateCreditExposureType(value: CreditExposureType) {
    return this.localizationService.translateEnum("creditExposureType", value);
  }

  translateCreditExposureState(value: CreditExposureState) {
    return this.localizationService.translateEnum("creditExposureState", value);
  }

  translateCreditExposureNotificationStatus(value: CreditExposureNotificationStatus) {
    return this.localizationService.translateEnum("creditExposureNotificationStatus", value);
  }
}
